import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Card,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Search,
  FilterList,
  Assignment,
  Business,
  FolderOpen,
  Share,
  PersonAdd,
  Close,
  Category,
  TrendingUp,
  Timeline,
  AttachMoney,
  AccessTime,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { useBrandColors } from '../hooks/useBrandColors';
import { useAuth } from '../contexts/AuthContext';
import { Project } from '../types';
import { ProjectPlanning } from './ProjectPlanning';
import { PREDEFINED_TASKS, getTaskColor, TASK_VALUES } from '../constants/tasks';
import timezoneService from '../services/timezoneService';

interface Client {
  id: string;
  name: string;
  email?: string;
  company?: string;
  phone?: string;
  address?: string;
  createdAt: string;
  updatedAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`projects-tabpanel-${index}`}
      aria-labelledby={`projects-tab-${index}`}
      {...other}
      style={{ height: value === index ? 'auto' : 0, overflow: 'hidden' }}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const ProjectsManager: React.FC = () => {
  const brandColors = useBrandColors();
  const { state } = useAuth();
  const isSuperOwner = state.user?.role === 'super_owner';
  const [tabValue, setTabValue] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  
  // Category dialog state
  const [categoryDialog, setCategoryDialog] = useState<{
    open: boolean;
    categoryName: string;
    isEdit: boolean;
    originalName?: string;
  }>({ open: false, categoryName: '', isEdit: false });

  // Client dialog state
  const [clientDialog, setClientDialog] = useState<{
    open: boolean;
    client: Partial<Client> | null;
    isEdit: boolean;
  }>({ open: false, client: null, isEdit: false });
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    project: Partial<Project & { clientId?: string; tasks?: string[] }> | null;
    isEdit: boolean;
  }>({ open: false, project: null, isEdit: false });

  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [newTask, setNewTask] = useState<string>('');

  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    project: Project | null;
    hasTimeEntries: boolean;
  }>({ open: false, project: null, hasTimeEntries: false });

  const [shareDialog, setShareDialog] = useState<{
    open: boolean;
    project: Project | null;
  }>({ open: false, project: null });

  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [projectMembers, setProjectMembers] = useState<any[]>([]);
  const [sharingLoading, setSharingLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [projectsData, categoriesData, clientsData] = await Promise.all([
        apiService.getProjects(),
        apiService.getCategories(),
        apiService.getClients()
      ]);
      setProjects(projectsData || []);
      setCategories(categoriesData || []);
      setClients(clientsData || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      // Set fallback data
      setProjects([]);
      setCategories(TASK_VALUES);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle client selection change (cascade to reset project)
  const handleClientChange = (clientId: string) => {
    setSelectedClient(clientId);
    setEditDialog({
      ...editDialog,
      project: editDialog.project
        ? { ...editDialog.project, clientId: clientId }
        : null,
    });
  };

  // Handle task management
  const handleAddTask = () => {
    if (newTask.trim() && !selectedTasks.includes(newTask.trim())) {
      const updatedTasks = [...selectedTasks, newTask.trim()];
      setSelectedTasks(updatedTasks);
      setEditDialog({
        ...editDialog,
        project: editDialog.project
          ? { 
              ...editDialog.project, 
              categoryType: updatedTasks[0] || '', // First task becomes primary
              categories: updatedTasks.slice(1) // Rest are additional
            }
          : null,
      });
      setNewTask('');
    }
  };

  const handleRemoveTask = (taskToRemove: string) => {
    const updatedTasks = selectedTasks.filter(task => task !== taskToRemove);
    setSelectedTasks(updatedTasks);
    setEditDialog({
      ...editDialog,
      project: editDialog.project
        ? { 
            ...editDialog.project, 
            categoryType: updatedTasks[0] || '', // First task becomes primary
            categories: updatedTasks.slice(1) // Rest are additional
          }
        : null,
    });
  };

  const handleAddProject = () => {
    setSelectedClient('');
    setSelectedTasks([]);
    setNewTask('');
    setEditDialog({
      open: true,
      project: { name: '', description: '', categoryType: '', clientId: '', categories: [] },
      isEdit: false,
    });
  };

  const handleEditProject = (project: Project) => {
    // Populate client selection
    const clientId = (project as any).clientId || '';
    setSelectedClient(clientId);
    
    // Populate tasks from project's categoryType and categories
    const projectTasks = [project.categoryType];
    if (project.categories && project.categories.length > 0) {
      projectTasks.push(...project.categories);
    }
    setSelectedTasks(projectTasks);
    
    setEditDialog({
      open: true,
      project: { 
        ...project,
        clientId: clientId
      },
      isEdit: true,
    });
  };

  const handleSaveProject = async () => {
    if (!editDialog.project?.name || !editDialog.project?.categoryType) return;

    try {
      const proj = editDialog.project as any;
      const projectData = {
        name: editDialog.project.name,
        description: editDialog.project.description || '',
        categoryType: editDialog.project.categoryType,
        categories: editDialog.project.categories || [],
        clientId: editDialog.project.clientId || null,
        // Billing fields
        isInternal: proj.isInternal !== false, // Default to true
        budgetHours: proj.budgetHours || null,
        budgetAmount: proj.budgetAmount || null,
        currency: proj.currency || 'USD',
        hourlyRate: proj.hourlyRate || null,
      };

      if (editDialog.isEdit && editDialog.project.id) {
        await apiService.updateProject(editDialog.project.id, projectData);
      } else {
        await apiService.createProject(projectData);
      }
      setEditDialog({ open: false, project: null, isEdit: false });
      loadData();
    } catch (error) {
      console.error('Failed to save project:', error);
      alert(`Failed to save project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteProject = async (id: string) => {
    const project = projects.find(p => p.id === id);
    if (!project) return;
    
    try {
      await apiService.deleteProject(id);
      loadData();
    } catch (error) {
      console.error('Failed to delete project:', error);
      
      // Check if it's the specific error about existing time entries
      if (error instanceof Error && error.message.includes('existing time entries')) {
        setDeleteDialog({
          open: true,
          project: project,
          hasTimeEntries: true
        });
      } else {
        alert(`Failed to delete project: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  const handleDeleteOption = async (option: 'cancel' | 'project-only' | 'force-delete') => {
    if (!deleteDialog.project) return;
    
    const projectName = deleteDialog.project.name;
    
    try {
      if (option === 'project-only') {
        await apiService.deleteProjectOnly(deleteDialog.project.id);
        alert(`Project "${projectName}" deleted. Time entries are now unassigned and can be reassigned to other projects.`);
        loadData();
      } else if (option === 'force-delete') {
        await apiService.forceDeleteProject(deleteDialog.project.id);
        alert(`Project "${projectName}" and all its time entries have been deleted.`);
        loadData();
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert(`Failed to delete project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setDeleteDialog({ open: false, project: null, hasTimeEntries: false });
    }
  };

  const formatDate = (dateString: string) => {
    // Convert UTC date string to user timezone and format
    const userDate = timezoneService.convertUTCToUserTime(dateString);
    return timezoneService.formatUserDate(userDate);
  };

  const getCategoryColor = (categoryType: string) => {
    return getTaskColor(categoryType);
  };

  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'No client';
  };

  // Project sharing handlers
  const handleOpenShareDialog = async (project: Project) => {
    setShareDialog({ open: true, project });
    setSharingLoading(true);
    
    try {
      const [members, projectMembersData] = await Promise.all([
        apiService.getTeamMembers(),
        apiService.getProjectMembers(project.id)
      ]);
      
      setTeamMembers(members || []);
      setProjectMembers(projectMembersData || []);
    } catch (error) {
      console.error('Failed to load sharing data:', error);
      setTeamMembers([]);
      setProjectMembers([]);
    } finally {
      setSharingLoading(false);
    }
  };

  const handleToggleMemberAccess = async (memberId: string, hasAccess: boolean) => {
    if (!shareDialog.project) return;
    
    try {
      if (hasAccess) {
        // Remove access
        await apiService.removeProjectMember(shareDialog.project.id, memberId);
      } else {
        // Grant access
        await apiService.addProjectMember(shareDialog.project.id, memberId, 'read');
      }
      
      // Reload project members
      const updatedMembers = await apiService.getProjectMembers(shareDialog.project.id);
      setProjectMembers(updatedMembers || []);
    } catch (error) {
      console.error('Failed to update member access:', error);
      alert(`Failed to update access: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Filter and search logic
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (project.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || project.categoryType === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return <Typography>Loading projects...</Typography>;
  }

  const styles = {
    container: {
      backgroundColor: 'white',
      minHeight: '100vh',
      padding: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '24px'
    }
  };

  // Category handlers
  const handleAddCategory = () => {
    setCategoryDialog({ open: true, categoryName: '', isEdit: false });
  };

  const handleSaveCategory = async () => {
    if (!categoryDialog.categoryName.trim()) return;
    const cleanCategoryName = categoryDialog.categoryName.toLowerCase().replace(/\s+/g, '');
    try {
      await apiService.createCategory(cleanCategoryName);
      await loadData();
      setCategoryDialog({ open: false, categoryName: '', isEdit: false });
    } catch (error) {
      console.error('Failed to save category:', error);
      alert(`Failed to save category: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteCategory = async (categoryName: string) => {
    const projectCount = projects.filter((p: Project) => p.categoryType === categoryName).length;
    if (projectCount > 0) {
      alert(`Cannot delete "${categoryName}" because it's used by ${projectCount} project(s).`);
      return;
    }
    if (window.confirm(`Delete category "${categoryName}"?`)) {
      try {
        await apiService.deleteCategory(categoryName);
        await loadData();
      } catch (error) {
        console.error('Failed to delete category:', error);
      }
    }
  };

  // Client handlers
  const handleAddClient = () => {
    setClientDialog({ open: true, client: { name: '', email: '', company: '', phone: '', address: '' }, isEdit: false });
  };

  const handleEditClient = (client: Client) => {
    setClientDialog({ open: true, client: { ...client }, isEdit: true });
  };

  const handleSaveClient = async () => {
    if (!clientDialog.client?.name) return;
    
    const clientData = {
      name: clientDialog.client.name,
      email: clientDialog.client.email,
      company: clientDialog.client.company,
      phone: clientDialog.client.phone,
      address: clientDialog.client.address,
    };
    
    try {
      if (clientDialog.isEdit && clientDialog.client.id) {
        await apiService.updateClient(clientDialog.client.id, clientData);
      } else {
        await apiService.createClient(clientData);
      }
      setClientDialog({ open: false, client: null, isEdit: false });
      await loadData();
    } catch (error) {
      console.error('Failed to save client:', error);
      alert(`Failed to save client: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (window.confirm('Delete this client?')) {
      try {
        await apiService.deleteClient(clientId);
        await loadData();
      } catch (error) {
        console.error('Failed to delete client:', error);
      }
    }
  };

  // Filter functions
  const filteredCategories = categories.filter((cat: string) =>
    cat.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const filteredClients = clients.filter((client: Client) =>
    client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (client.company && client.company.toLowerCase().includes(clientSearch.toLowerCase()))
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <FolderOpen sx={{ fontSize: '32px', color: brandColors.primary }} />
        <h1 style={{ ...styles.title, color: brandColors.textPrimary }}>Projects & Settings</h1>
      </div>
      
      <p style={styles.subtitle}>
        Manage your projects, task categories, and clients.
      </p>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab icon={<FolderOpen />} label="Projects" />
          <Tab icon={<Assignment />} label="Tasks" />
          <Tab icon={<Business />} label="Clients" />
          {isSuperOwner && <Tab icon={<TrendingUp />} label="Planning" />}
        </Tabs>
      </Box>

      {/* Projects Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddProject} sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}>
            Add Project
          </Button>
        </Box>
        <Paper sx={{ p: 3 }}>

      {/* Search and Filter Section */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search projects..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Task</InputLabel>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            label="Task"
            startAdornment={
              <FilterList sx={{ color: 'text.secondary', mr: 1 }} />
            }
          >
            <MenuItem value="">All Tasks</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category} value={category}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={category}
                    size="small"
                    color={getCategoryColor(category)}
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {(searchTerm || categoryFilter) && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSearchTerm('');
              setCategoryFilter('');
            }}
            sx={{ textTransform: 'none' }}
          >
            Clear Filters
          </Button>
        )}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Client
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Project Name
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Description
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Task
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Created
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects.map((project) => (
              <TableRow key={project.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {getClientName((project as any).clientId)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assignment sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      {project.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={project.description || 'No description'} arrow>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: project.description ? 'help' : 'default',
                      }}
                    >
                      {project.description || 'No description'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip
                      label={project.categoryType}
                      size="small"
                      color={getCategoryColor(project.categoryType)}
                      sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                    />
                    {project.categories && project.categories.length > 0 && 
                      project.categories.map((category) => (
                        <Chip
                          key={category}
                          label={category}
                          size="small"
                          color={getCategoryColor(category)}
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ))
                    }
                  </Box>
                </TableCell>
                <TableCell>{formatDate(project.createdAt)}</TableCell>
                <TableCell>
                  <Tooltip title="Share project">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenShareDialog(project)}
                      color="primary"
                    >
                      <Share />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit project">
                    <IconButton
                      size="small"
                      onClick={() => handleEditProject(project)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete project">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteProject(project.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {projects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Assignment sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      📁 No projects yet
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', maxWidth: 400, mx: 'auto' }}>
                      Projects help you organize your time tracking. Create tasks and clients in the 
                      Manage section first, then create projects here.
                    </Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<Add />} 
                      onClick={handleAddProject}
                      sx={{
                        backgroundColor: '#27ae60',
                        '&:hover': {
                          backgroundColor: '#219a52',
                        },
                      }}
                    >
                      Create Your First Project
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredProjects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      No projects match your search
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      Try adjusting your search terms or filters
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        setCategoryFilter('');
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Clear all filters
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      </Paper>

      {/* Edit/Add Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => {
          setEditDialog({ open: false, project: null, isEdit: false });
          setSelectedClient('');
          setSelectedTasks([]);
          setNewTask('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editDialog.isEdit ? 'Edit Project' : 'Create New Project'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            {/* Hierarchical Flow Visualization */}
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 2,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#666' }}>
                Project Structure: Client → Project → Tasks
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Business sx={{ fontSize: 16, color: '#666' }} />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {selectedClient ? clients.find(c => c.id === selectedClient)?.name : 'No Client'} 
                  </Typography>
                </Box>
                <Typography sx={{ color: '#666' }}>→</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assignment sx={{ fontSize: 16, color: '#666' }} />
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {editDialog.project?.name || 'Project Name'}
                  </Typography>
                </Box>
                <Typography sx={{ color: '#666' }}>→</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {selectedTasks.length > 0 ? `${selectedTasks.length} tasks` : 'No tasks yet'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Client Selection */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                1. Select Client (Optional)
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedClient}
                  onChange={(e) => handleClientChange(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>No client</em>
                  </MenuItem>
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {client.name}
                          </Typography>
                          {client.company && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {client.company}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Project Details */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                2. Project Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Project Name"
                  required
                  fullWidth
                  value={editDialog.project?.name || ''}
                  onChange={(e) =>
                    setEditDialog({
                      ...editDialog,
                      project: editDialog.project
                        ? { ...editDialog.project, name: e.target.value }
                        : null,
                    })
                  }
                />
                <TextField
                  label="Description"
                  multiline
                  rows={3}
                  fullWidth
                  value={editDialog.project?.description || ''}
                  onChange={(e) =>
                    setEditDialog({
                      ...editDialog,
                      project: editDialog.project
                        ? { ...editDialog.project, description: e.target.value }
                        : null,
                    })
                  }
                />
              </Box>
            </Box>

            {/* Task Management */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                3. Project Tasks
              </Typography>
              
              {/* Current Tasks */}
              {selectedTasks.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    Current tasks:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedTasks.map((task, index) => (
                      <Chip
                        key={task}
                        label={`${index + 1}. ${task}`}
                        size="small"
                        color={getCategoryColor(task)}
                        onDelete={() => handleRemoveTask(task)}
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Add New Task */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label="Add task"
                  size="small"
                  fullWidth
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTask();
                    }
                  }}
                  placeholder="Enter task name..."
                />
                <Button
                  variant="outlined"
                  onClick={handleAddTask}
                  disabled={!newTask.trim() || selectedTasks.includes(newTask.trim())}
                  sx={{ minWidth: 80 }}
                >
                  Add
                </Button>
              </Box>

              {/* Predefined Tasks Quick Add */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Quick add common tasks:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {TASK_VALUES.map((category) => (
                    <Chip
                      key={category}
                      label={category}
                      size="small"
                      color={getCategoryColor(category)}
                      variant={selectedTasks.includes(category) ? "filled" : "outlined"}
                      onClick={() => {
                        if (!selectedTasks.includes(category)) {
                          setNewTask(category);
                          const updatedTasks = [...selectedTasks, category];
                          setSelectedTasks(updatedTasks);
                          setEditDialog({
                            ...editDialog,
                            project: editDialog.project
                              ? { 
                                  ...editDialog.project, 
                                  categoryType: updatedTasks[0] || '',
                                  categories: updatedTasks.slice(1)
                                }
                              : null,
                          });
                          setNewTask('');
                        }
                      }}
                      sx={{ 
                        fontSize: '0.75rem',
                        cursor: selectedTasks.includes(category) ? 'default' : 'pointer',
                        opacity: selectedTasks.includes(category) ? 0.6 : 1,
                        '&:hover': selectedTasks.includes(category) ? {} : {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                  Standard tasks are always available. Already selected tasks are shown in filled style.
                </Typography>
              </Box>

              {selectedTasks.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Add at least one task to this project. Tasks help organize your time tracking.
                  </Typography>
                </Alert>
              )}
            </Box>

            {/* Budget & Billing Section */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney sx={{ fontSize: 18 }} />
                4. Budget & Billing (Optional)
              </Typography>
              
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f8f9fa', 
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}>
                {/* Internal Project Toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box
                    onClick={() => setEditDialog({
                      ...editDialog,
                      project: editDialog.project
                        ? { ...editDialog.project, isInternal: !(editDialog.project as any).isInternal }
                        : null,
                    })}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: (editDialog.project as any)?.isInternal !== false ? '#e8f5e9' : 'transparent',
                      border: '1px solid',
                      borderColor: (editDialog.project as any)?.isInternal !== false ? '#4caf50' : '#e0e0e0',
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        border: '2px solid',
                        borderColor: (editDialog.project as any)?.isInternal !== false ? '#4caf50' : '#ccc',
                        backgroundColor: (editDialog.project as any)?.isInternal !== false ? '#4caf50' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px'
                      }}
                    >
                      {(editDialog.project as any)?.isInternal !== false && '✓'}
                    </Box>
                    <Typography variant="body2">
                      Internal project (hours only)
                    </Typography>
                  </Box>
                  
                  <Chip
                    label={(editDialog.project as any)?.isInternal !== false ? 'Internal' : 'Billable 💰'}
                    size="small"
                    color={(editDialog.project as any)?.isInternal !== false ? 'default' : 'success'}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                {/* Billing fields - only shown if NOT internal */}
                {(editDialog.project as any)?.isInternal === false && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Currency</InputLabel>
                        <Select
                          value={(editDialog.project as any)?.currency || 'USD'}
                          label="Currency"
                          onChange={(e) => setEditDialog({
                            ...editDialog,
                            project: editDialog.project
                              ? { ...editDialog.project, currency: e.target.value }
                              : null,
                          })}
                        >
                          <MenuItem value="USD">USD ($)</MenuItem>
                          <MenuItem value="SEK">SEK (kr)</MenuItem>
                          <MenuItem value="EUR">EUR (€)</MenuItem>
                          <MenuItem value="NOK">NOK (kr)</MenuItem>
                          <MenuItem value="DKK">DKK (kr)</MenuItem>
                          <MenuItem value="GBP">GBP (£)</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <TextField
                        label="Hourly Rate"
                        type="number"
                        size="small"
                        value={(editDialog.project as any)?.hourlyRate || ''}
                        onChange={(e) => setEditDialog({
                          ...editDialog,
                          project: editDialog.project
                            ? { ...editDialog.project, hourlyRate: e.target.value ? parseFloat(e.target.value) : null }
                            : null,
                        })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><AttachMoney sx={{ fontSize: 16 }} /></InputAdornment>,
                        }}
                        sx={{ flex: 1 }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="Budget Hours"
                        type="number"
                        size="small"
                        value={(editDialog.project as any)?.budgetHours || ''}
                        onChange={(e) => setEditDialog({
                          ...editDialog,
                          project: editDialog.project
                            ? { ...editDialog.project, budgetHours: e.target.value ? parseFloat(e.target.value) : null }
                            : null,
                        })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><AccessTime sx={{ fontSize: 16 }} /></InputAdornment>,
                        }}
                        sx={{ flex: 1 }}
                      />
                      
                      <TextField
                        label="Budget Amount"
                        type="number"
                        size="small"
                        value={(editDialog.project as any)?.budgetAmount || ''}
                        onChange={(e) => setEditDialog({
                          ...editDialog,
                          project: editDialog.project
                            ? { ...editDialog.project, budgetAmount: e.target.value ? parseFloat(e.target.value) : null }
                            : null,
                        })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><AttachMoney sx={{ fontSize: 16 }} /></InputAdornment>,
                        }}
                        sx={{ flex: 1 }}
                      />
                    </Box>

                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Billable projects will show in financial reports. Leave budget fields empty for unlimited.
                    </Typography>
                  </Box>
                )}

                {(editDialog.project as any)?.isInternal !== false && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Internal projects only track hours worked. Uncheck to enable billing options.
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => {
              setEditDialog({ open: false, project: null, isEdit: false });
              setSelectedClient('');
              setSelectedTasks([]);
              setNewTask('');
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveProject} 
            variant="contained"
            disabled={!editDialog.project?.name || selectedTasks.length === 0}
            sx={{
              backgroundColor: '#27ae60',
              '&:hover': {
                backgroundColor: '#219a52',
              },
            }}
          >
            {editDialog.isEdit ? 'Update Project' : 'Create Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, project: null, hasTimeEntries: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete />
          Delete Project
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Cannot delete "{deleteDialog.project?.name}"</AlertTitle>
            This project has existing time entries and cannot be deleted directly.
          </Alert>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            Choose how you want to proceed:
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDeleteOption('project-only')}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2,
                borderColor: 'warning.main',
                color: 'warning.main',
                '&:hover': {
                  borderColor: 'warning.dark',
                  backgroundColor: 'warning.light',
                  color: 'warning.dark',
                },
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  1. Delete project only
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Time entries become unassigned and can be reassigned to other projects
                </Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDeleteOption('force-delete')}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2,
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': {
                  borderColor: 'error.dark',
                  backgroundColor: 'error.light',
                  color: 'error.dark',
                },
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  2. Delete project AND all its time entries
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  ⚠️ Permanent deletion - this cannot be undone
                </Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={() => handleDeleteOption('cancel')}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2,
                borderColor: 'grey.400',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'grey.50',
                },
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  3. Cancel
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Keep everything as it is
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Share Project Dialog */}
      <Dialog
        open={shareDialog.open}
        onClose={() => setShareDialog({ open: false, project: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Share />
          Share Project: {shareDialog.project?.name}
        </DialogTitle>
        <DialogContent>
          {sharingLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography>Loading team members...</Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Select which team members can access this project:
              </Typography>
              
              {teamMembers.length === 0 ? (
                <Alert severity="info">
                  <AlertTitle>No team members found</AlertTitle>
                  Invite team members first to share projects with them.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {teamMembers
                    .filter(member => member.status === 'active')
                    .map((member) => {
                      const hasAccess = projectMembers.some(pm => pm.user_id === member.id);
                      return (
                        <Box
                          key={member.id}
                          sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: hasAccess ? 'primary.main' : 'grey.300',
                            borderRadius: 1,
                            backgroundColor: hasAccess ? 'primary.light' : 'transparent',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: hasAccess ? 'primary.light' : 'grey.50',
                            },
                          }}
                          onClick={() => handleToggleMemberAccess(member.id, hasAccess)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '4px',
                                  border: '2px solid',
                                  borderColor: hasAccess ? 'primary.main' : 'grey.400',
                                  backgroundColor: hasAccess ? 'primary.main' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                }}
                              >
                                {hasAccess && '✓'}
                              </Box>
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {member.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {member.email} • {member.role}
                                </Typography>
                              </Box>
                            </Box>
                            {hasAccess && (
                              <Chip 
                                label="Has Access" 
                                size="small" 
                                color="primary" 
                                sx={{ fontSize: '0.75rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              )}
              
              {projectMembers.length > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    {projectMembers.length} member{projectMembers.length !== 1 ? 's' : ''} {projectMembers.length !== 1 ? 'have' : 'has'} access to this project
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setShareDialog({ open: false, project: null })}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
      </TabPanel>

      {/* Tasks Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddCategory} sx={{ backgroundColor: '#27ae60', '&:hover': { backgroundColor: '#219a52' } }}>
            Add Task
          </Button>
        </Box>
        <Paper sx={{ p: 3 }}>
          <TextField
            placeholder="Search tasks..."
            variant="outlined"
            size="small"
            value={categorySearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCategorySearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary' }} /></InputAdornment> }}
            sx={{ mb: 3, minWidth: 300 }}
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Task Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Projects Using</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCategories.map((category: string) => {
                  const projectCount = projects.filter((p: Project) => p.categoryType === category).length;
                  const isPredefined = (TASK_VALUES as readonly string[]).includes(category);
                  return (
                    <TableRow key={category} hover>
                      <TableCell><Chip label={category} color={getCategoryColor(category)} variant="outlined" size="small" /></TableCell>
                      <TableCell><Chip label={isPredefined ? 'Predefined' : 'Custom'} size="small" color={isPredefined ? 'primary' : 'default'} variant="outlined" /></TableCell>
                      <TableCell>{projectCount} project(s)</TableCell>
                      <TableCell>
                        {!isPredefined && projectCount === 0 && (
                          <Tooltip title="Delete task"><IconButton size="small" color="error" onClick={() => handleDeleteCategory(category)}><Delete fontSize="small" /></IconButton></Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredCategories.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>No tasks found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Planning Tab - Super Owner Only */}
      {isSuperOwner && (
        <TabPanel value={tabValue} index={3}>
          <ProjectPlanning />
        </TabPanel>
      )}

      {/* Clients Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddClient} sx={{ backgroundColor: '#3498db', '&:hover': { backgroundColor: '#2980b9' } }}>
            Add Client
          </Button>
        </Box>
        <Paper sx={{ p: 3 }}>
          <TextField
            placeholder="Search clients..."
            variant="outlined"
            size="small"
            value={clientSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary' }} /></InputAdornment> }}
            sx={{ mb: 3, minWidth: 300 }}
          />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Client Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClients.map((client: Client) => (
                  <TableRow key={client.id} hover>
                    <TableCell><Typography variant="subtitle2" sx={{ fontWeight: 500 }}>{client.name}</Typography></TableCell>
                    <TableCell>{client.company || '-'}</TableCell>
                    <TableCell>{client.email || '-'}</TableCell>
                    <TableCell>{client.phone || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit client"><IconButton size="small" onClick={() => handleEditClient(client)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete client"><IconButton size="small" color="error" onClick={() => handleDeleteClient(client.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredClients.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>No clients found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {/* Category Dialog */}
      <Dialog open={categoryDialog.open} onClose={() => setCategoryDialog({ open: false, categoryName: '', isEdit: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Task</DialogTitle>
        <DialogContent>
          <TextField label="Task Name" required fullWidth value={categoryDialog.categoryName} placeholder="Enter task name" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCategoryDialog({ ...categoryDialog, categoryName: e.target.value })} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialog({ open: false, categoryName: '', isEdit: false })}>Cancel</Button>
          <Button onClick={handleSaveCategory} variant="contained" disabled={!categoryDialog.categoryName.trim()}>Create Task</Button>
        </DialogActions>
      </Dialog>

      {/* Client Dialog */}
      <Dialog open={clientDialog.open} onClose={() => setClientDialog({ open: false, client: null, isEdit: false })} maxWidth="sm" fullWidth>
        <DialogTitle>{clientDialog.isEdit ? 'Edit Client' : 'Add New Client'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField label="Client Name" required fullWidth value={clientDialog.client?.name || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientDialog({ ...clientDialog, client: clientDialog.client ? { ...clientDialog.client, name: e.target.value } : null })} />
            <TextField label="Company" fullWidth value={clientDialog.client?.company || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientDialog({ ...clientDialog, client: clientDialog.client ? { ...clientDialog.client, company: e.target.value } : null })} />
            <TextField label="Email" type="email" fullWidth value={clientDialog.client?.email || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientDialog({ ...clientDialog, client: clientDialog.client ? { ...clientDialog.client, email: e.target.value } : null })} />
            <TextField label="Phone" fullWidth value={clientDialog.client?.phone || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientDialog({ ...clientDialog, client: clientDialog.client ? { ...clientDialog.client, phone: e.target.value } : null })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientDialog({ open: false, client: null, isEdit: false })}>Cancel</Button>
          <Button onClick={handleSaveClient} variant="contained" disabled={!clientDialog.client?.name}>{clientDialog.isEdit ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
